import { flooriRoutes } from '@floori-web/constants';
import { CompanyConfigBasicDto, CompanyConfigDetailsDto } from '@floori/models';
import { Inject, Injectable } from '@angular/core';
import { FLOORI_ENDPOINTS, FLOORI_ENV } from '@floori-web-components';
import { FlooriEnv } from '@floori-web/models';
import { MosaicEnv } from '@floori-mosaic/api';

@Injectable()
export class SetupService {
    private readonly pathnameKey = flooriRoutes['preview'];
    private _clientKey = '';

    get clientKey(): string {
        return this._clientKey;
    }

    get endpoints(): Record<string, Record<string, string>> {
        return this._endpoints;
    }

    constructor(
        @Inject(FLOORI_ENV) private environment: FlooriEnv | MosaicEnv,
        @Inject(FLOORI_ENDPOINTS) private _endpoints: Record<string, Record<string, string>>,
    ) {}

    bootstrap(): Promise<CompanyConfigBasicDto> {
        const { hostname, pathname } = window.location;
        const pathSegments = pathname?.split('/').filter(p => p);
        const fromPreview =
            pathSegments?.[0]?.toLowerCase()?.trim() === this.pathnameKey &&
            pathSegments?.length > 1;
        if (fromPreview) {
            this._clientKey = pathSegments?.[1];
            return this.fetchCompanyInfoBasic(this._clientKey);
        }

        let companyName = hostname?.split('.').filter(p => p)?.[0];
        if (companyName === 'staging' || hostname === 'staging.floori.io' || companyName === 'localhost' || hostname?.includes('localhost')) {
            companyName = 'andersens';
        }

        return this.fetchVendorId(companyName).then(vendorId => {
            this._clientKey = vendorId;
            return this.fetchCompanyInfoBasic(this._clientKey);
        });
    }

    async fetchCompanyInfoBasic(clientKey: string): Promise<CompanyConfigBasicDto> {
        const url = `${this.environment.apiUrl}/${this._endpoints['company']['config']}`;
        return await fetch(url, {
            method: 'GET',
            headers: {
                authorization: `Bearer ${clientKey}`,
            },
        }).then(res => res.json());
    }

    async fetchCompanyInfoDetails(clientKey: string): Promise<CompanyConfigDetailsDto> {
        const url = `${this.environment.apiUrl}/${this._endpoints['company']['configDetails']}`;
        try {
            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${clientKey}`,
                },
            });

            if (!res.ok) {
                throw new Error(
                    `[FLOORI-SETUP] Failed to fetch company details. Status: ${res.status}`,
                );
            }

            return await res.json();
        } catch (err) {
            throw new Error(`[FLOORI-SETUP] Error fetching company details. ${err}`);
        }
    }

    private async fetchVendorId(companyName: string): Promise<string> {
        const url = `${this.environment.apiUrl}/${this._endpoints['company']['vendorId']}`.replace(
            ':subdomain',
            companyName,
        );
        return await fetch(url, {
            method: 'GET',
        })
            .then(res => res.json())
            .then(body => body?.vendorId);
    }
}
